// ─────────────────────────────────────────────────────────────────────────────
// Attention — the Look node's decision logic, kept pure (no THREE, no DOM, no
// clock) so it runs under `node --test`. lookRuntime.js feeds it the camera
// snapshot each frame and writes its outputs into the shared look target
// (lookTargetRuntime.js) that Head Follow and Eye Follow aim at.
//
// The four states the Look node shows:
//   Eye contact (hold)    — engaged, held there by the node's `holdEyeContact`
//                           input (e.g. while speaking): nothing ends it.
//   Eye contact (dynamic) — engaged because the camera moved in view; ends when
//                           it has been still for Lose interest after, or out
//                           of reach. Broken up by brief glances away.
//   Looking around        — glances around in front of the face; small glances
//                           move only the eyes; checks in on the camera now and
//                           then.
//   Inactive              — hands head and eyes back to the animation
//                           (fades over Release time). The node's `active` input.
// Internally the modes are 'engaged' / 'wandering' / 'inactive'; `holding`
// tells the two kinds of eye contact apart (attentionStateLabel).
//
// Modelled on the Unreal DMHAK look logic (random look-target timer,
// TryLookAtPlayerOrLookAway, the awareness tick), with its quirks fixed: gaze
// jumps are measured as directions from the face (not world positions from the
// origin), and the state switching uses plain timers.
//
// Conventions: settings are in DEGREES / SECONDS / METRES; outputs are RADIANS.
// Yaw/pitch are body-relative, +yaw = toward the character's left, +pitch = up.
// ─────────────────────────────────────────────────────────────────────────────

const DEG = Math.PI / 180

/** Every setting, with its default. Mirrors the node's config pins. */
export const ATTENTION_DEFAULTS = Object.freeze({
  active: 1,
  // Noticing / eye contact
  noticeAngle: 60,        // camera within this many degrees of body-forward can be noticed
  noticeMove: 0.05,       // metres of camera movement that counts as "you did something"
  loseInterestAfter: 4,   // camera still this long (s) → look away
  minEyeContact: 1.5,     // stay engaged at least this long (s)
  giveUpAfter: 1,         // camera out of view this long (s) while engaged → look away
  blinkOnSettle: 1,       // blink once when contact lands
  blinkOnLookAway: 1,     // blink when breaking contact
  blinkOnGrab: 1,         // blink when the user grabs the camera (while in view)
  headSpeed: 0.08,        // how fast the head follows its target — during eye contact (dragging or
                          // not) and on the turn away (Head Follow smoothing units; replaces its Smoothing)
  headStartDelayMin: 0.1, // during eye contact the HEAD waits a random delay in min..max (s) before
  headStartDelayMax: 0.7,  //   each move, so the eyes go first and the head follows
  transitionMin: 0.5,     // seconds for the head to turn to you / away when eye contact starts or
  transitionMax: 0.9,     //   ends (random in min..max each time; eased in/out)
  releaseTime: 0.5,       // seconds to fade out when going inactive (and back in)
  // Glancing away briefly DURING eye contact (eyes + a dialed amount of head)
  glanceAwayEveryMin: 3,  // a brief glance away every this-many seconds (random in min..max)
  glanceAwayEveryMax: 8,  //   — max 0 = never
  glanceAwayLengthMin: 0.8, // each lasts this long (s, random in min..max), then back to the camera
  glanceAwayLengthMax: 3,
  // (where it lands, the head's share/delay/speed, eyes-only chance and blink
  //  all come from the Looking around settings — it is a look-around glance)
  // Checking in WHILE wandering (eyes + a dialed amount of head look back at the camera)
  checkInEveryMin: 4,     // a brief look back at the camera every this-many seconds — max 0 = never
  checkInEveryMax: 10,
  checkInLengthMin: 1.2,  // each lasts this long (s), then back to looking around
  checkInLengthMax: 4.5,
  // (the head's share/delay/speed, eyes-only chance and blink come from the
  //  Looking around settings)
  // Wandering gaze
  holdMin: 0.6,           // shortest glance (s)
  holdMax: 4.2,           // longest glance (s)
  wanderYaw: 25,          // glances land within ±this many degrees left/right
  wanderPitch: 10,        // … and ±this many up/down
  stickiness: 1,          // after a short glance, how much the next one stays near it (0..1)
  headGlanceAmount: 0.5,  // share of each glance the head follows (0 = eyes only, 1 = all the way)
  glanceHeadDelayMin: 0.05, // while looking around the HEAD waits a random delay in min..max (s)
  glanceHeadDelayMax: 1.8, //   before each move, so the eyes go first
  eyesOnlyChance: 0.4,   // chance a glance moves only the eyes
  eyesOnlyBelow: 2,       // glances smaller than this (deg) are always eyes-only
  blinkOnJump: 6,         // a glance bigger than this (deg) triggers a blink
  glanceHeadSpeed: 0.07,   // head speed for glances (bigger glances go a little slower)
})

export const STATE_CODE = Object.freeze({ inactive: 0, wandering: 1, engaged: 2 })

/** The four states, as the Look node shows them. */
export const STATE_LABEL = Object.freeze({
  hold: 'Eye contact (hold)',
  engaged: 'Eye contact (dynamic)',
  wandering: 'Looking around',
  inactive: 'Inactive',
})

export function attentionStateLabel(st) {
  if (st.mode === 'engaged') return st.holding ? STATE_LABEL.hold : STATE_LABEL.engaged
  return STATE_LABEL[st.mode] ?? STATE_LABEL.inactive
}

// After re-activation, a camera move this recent (s) counts as "just noticed".
const REACTIVATE_RECENT = 1.0
// Ignore the settle check for this long after engaging — the eyes of the
// PREVIOUS glance may momentarily pass through the camera on the way.
const SETTLE_GRACE = 0.15
// The eyes go all the way to their target; this is only how close counts as
// "eye contact has landed" for the one settle blink (breathing keeps the exact
// error from ever reaching zero).
const SETTLE_DEG = 1.5

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v)
function mapClamped(v, inA, inB, outA, outB) {
  const t = clamp((v - inA) / (inB - inA), 0, 1)
  return outA + (outB - outA) * t
}

/** Angle (deg) between two body-relative yaw/pitch directions (rad). */
export function angleBetweenDeg(yawA, pitchA, yawB, pitchB) {
  const ca = Math.cos(pitchA), cb = Math.cos(pitchB)
  const dot = Math.sin(yawA) * ca * Math.sin(yawB) * cb
    + Math.sin(pitchA) * Math.sin(pitchB)
    + Math.cos(yawA) * ca * Math.cos(yawB) * cb
  return Math.acos(clamp(dot, -1, 1)) / DEG
}

export function createAttention() {
  return {
    mode: 'wandering',
    time: 0,
    modeTime: 0,
    gate: 1,
    // camera movement
    anchorSet: false, ax: 0, ay: 0, az: 0,
    lastMoveAt: 0,
    lastGrabId: 0,
    holding: false,         // eye contact held by the holdEyeContact input
    outOfViewFor: 0,
    settledBlinkDone: false,
    // glances (body-relative, rad)
    eyeYaw: 0, eyePitch: 0,
    headYaw: 0, headPitch: 0,
    holdLeft: 0,
    // Brief glance away during eye contact
    glancingAway: false,
    glanceAwayIn: 0,        // seconds until the next one
    glanceAwayLeft: 0,      // seconds left in the current one
    gaYaw: 0, gaPitch: 0,   // its offset from the camera (rad)
    gaHeadMoved: false,     // the head joined this glance away (it wasn't eyes-only)
    // Check-in while wandering
    checkingIn: false,
    checkInIn: 0,
    checkInLeft: 0,
    ciHeadMoved: false,     // the head joined this check-in
    holdDuration: 0,
    headSmoothing: 0,
    // State transition glides, one for the head and one for the eyes so they
    // can move at different speeds: `v` (0 = camera, 1 = glance) eases from
    // `from` to `to` over `dur` seconds instead of jumping.
    headGlide: { v: 1, from: 1, to: 1, t: 1, dur: 0 },
    eyeGlide: { v: 1, from: 1, to: 1, t: 1, dur: 0 },
    // The head lags the eyes: each head move waits in this slot for its random
    // delay (Head start delay / Glance head delay). A newer move replaces one
    // that hasn't started; fields it leaves null keep the pending move's value.
    headPending: { active: false, left: 0, glideTo: null, glideDur: 0, setDir: false, yaw: 0, pitch: 0, ga: null, smoothing: 0 },
    headGa: false,          // the head is tracking a brief glance away (camera + offset × share)
    lastJumpDeg: 0,
    lastEyesOnly: false,
    // per-step outputs
    out: {
      gate: 1, away: 1, eyeAway: 1,
      headYaw: 0, headPitch: 0, eyeYaw: 0, eyePitch: 0,
      headSmoothing: 0,
      blink: false,
      transition: null, // e.g. 'wandering→engaged' on the step it happened
    },
  }
}

function setMode(st, mode) {
  st.out.transition = `${st.mode}→${mode}`
  st.mode = mode
  st.modeTime = 0
}

function requestBlink(st) {
  st.out.blink = true
}

/**
 * Start a glide toward `to` (0 = camera, 1 = glance). The duration scales
 * with how far there is to go, so reversing halfway through a glide takes half
 * the time rather than starting the full glide over.
 */
function startGlide(g, to, seconds) {
  g.from = g.v
  g.to = to
  g.t = 0
  g.dur = Math.max(0, seconds) * Math.abs(to - g.v)
}

function stepGlide(g, dt) {
  if (g.t >= 1) return
  g.t = g.dur > 1e-4 ? Math.min(1, g.t + dt / g.dur) : 1
  const ease = g.t * g.t * (3 - 2 * g.t) // smoothstep: eases in and out
  g.v = g.from + (g.to - g.from) * ease
}

const randIn = (rand, a, b) => a + rand() * Math.max(0, b - a)

/**
 * Queue a head move to start after `delay` seconds. h: { glideTo (0 camera ..
 * 1 look-around direction, or null to leave it), glideDur, setDir + yaw/pitch
 * (the look-around head direction), ga (track a brief glance away, or null),
 * smoothing (head speed, or 0 to leave it) }.
 */
function queueHead(st, delay, h) {
  const p = st.headPending
  const keep = p.active
  if (h.glideTo != null || !keep) { p.glideTo = h.glideTo ?? null; p.glideDur = h.glideDur ?? 0 }
  if (h.setDir || !keep) { p.setDir = !!h.setDir; p.yaw = h.yaw ?? 0; p.pitch = h.pitch ?? 0 }
  if (h.ga != null || !keep) p.ga = h.ga ?? null
  if (h.smoothing || !keep) p.smoothing = h.smoothing ?? 0
  p.active = true
  p.left = delay
  if (delay <= 0) applyHead(st)
}

function applyHead(st) {
  const p = st.headPending
  p.active = false
  if (p.setDir) { st.headYaw = p.yaw; st.headPitch = p.pitch }
  if (p.glideTo != null) startGlide(st.headGlide, p.glideTo, p.glideDur)
  if (p.ga != null) st.headGa = p.ga
  if (p.smoothing) st.headSmoothing = p.smoothing
}

const headStartDelay = (cfg, rand) => randIn(rand, cfg.headStartDelayMin, cfg.headStartDelayMax)

/** Looking-around nuance shared by every glance: does the head join a glance of
 *  `jumpDeg`, and does it blink? (Eyes-only chance / below, Blink on glance over.) */
function eyesOnlyFor(cfg, rand, jumpDeg) {
  return rand() < cfg.eyesOnlyChance || jumpDeg < cfg.eyesOnlyBelow
}
/** Look-around head speed for a glance of `jumpDeg` — bigger glances go a little
 *  slower (Unreal maps 0..10° to 1.2→0.65). */
const glanceSpeed = (cfg, jumpDeg) => cfg.glanceHeadSpeed * mapClamped(jumpDeg, 0, 10, 1, 0.55)
const glanceHeadDelay = (cfg, rand) => randIn(rand, cfg.glanceHeadDelayMin, cfg.glanceHeadDelayMax)
const transitionTime = (cfg, rand) => randIn(rand, cfg.transitionMin, cfg.transitionMax)

function enterEngaged(st, cfg, rand) {
  setMode(st, 'engaged')
  st.glancingAway = false
  st.glanceAwayIn = randIn(rand, cfg.glanceAwayEveryMin, cfg.glanceAwayEveryMax)
  // Eyes straight onto the camera; the head follows after its delay, turning
  // over the transition time.
  startGlide(st.eyeGlide, 0, 0)
  queueHead(st, headStartDelay(cfg, rand), {
    glideTo: 0, glideDur: transitionTime(cfg, rand), ga: false, smoothing: cfg.headSpeed,
  })
  st.settledBlinkDone = false
  st.outOfViewFor = 0
}

/**
 * Pick the next glance. `first` = the first glance after breaking contact or
 * waking up: no pull back toward the previous glance, and the head always
 * joins in (a look-away is a head movement, not just the eyes).
 */
function pickGlance(st, cfg, rand, first) {
  const W = cfg.wanderYaw * DEG
  const H = cfg.wanderPitch * DEG
  let ny = (rand() * 2 - 1) * W
  let np = (rand() * 2 - 1) * H

  // Stickiness — Unreal's InterpolatePreviousLookTarget: after a SHORT hold the
  // next glance stays close to the last one (alpha up to 1 = stay put); after
  // a long hold it can roam. stickiness scales that pull.
  if (!first) {
    const pull = st.holdDuration < 1.2
      ? mapClamped(st.holdDuration, 0, 1.2, 1.0, 0.1)
      : 0.1 + rand() * 0.2
    const a = clamp(pull * cfg.stickiness, 0, 1)
    ny += (st.eyeYaw - ny) * a
    np += (st.eyePitch - np) * a
  }

  const jump = angleBetweenDeg(st.eyeYaw, st.eyePitch, ny, np)
  const prevHold = st.holdDuration
  const eyesOnly = !first && (eyesOnlyFor(cfg, rand, jump) || prevHold < 0.5)

  st.eyeYaw = ny
  st.eyePitch = np
  if (!eyesOnly) {
    // The head follows Head Glance Amount of the glance, after its delay. The
    // first glance (the look-away) also turns it off the camera, over the
    // transition time.
    const amt = clamp(cfg.headGlanceAmount, 0, 1)
    queueHead(st, glanceHeadDelay(cfg, rand), {
      setDir: true, yaw: ny * amt, pitch: np * amt,
      glideTo: first ? 1 : null, glideDur: first ? transitionTime(cfg, rand) : 0,
      ga: first ? false : null,
      smoothing: first ? cfg.headSpeed : glanceSpeed(cfg, jump),
    })
  }
  if (jump > cfg.blinkOnJump) requestBlink(st)

  st.lastJumpDeg = jump
  st.lastEyesOnly = eyesOnly
  st.holdDuration = cfg.holdMin + rand() * Math.max(0, cfg.holdMax - cfg.holdMin)
  st.holdLeft = st.holdDuration
}

function enterWandering(st, cfg, rand, scene, lookingAway) {
  setMode(st, 'wandering')
  st.checkingIn = false
  st.checkInIn = randIn(rand, cfg.checkInEveryMin, cfg.checkInEveryMax)
  startGlide(st.eyeGlide, 1, 0)
  if (lookingAway) {
    if (cfg.blinkOnLookAway) requestBlink(st)
    // The eyes were on the camera: measure the first glance from there.
    st.eyeYaw = scene.camYaw
    st.eyePitch = scene.camPitch
  }
  pickGlance(st, cfg, rand, true)
}

// ── Brief glance away during eye contact ──────────────────────────────────────
// A look-around glance taken around the camera: it lands within Glance width/
// height of the camera, the head joins with Head Glance Amount after a Glance
// Head Delay at Glance head speed (unless it's eyes-only), a big one blinks —
// then back. It isn't a state change: the boredom clock keeps running. The
// offset rides on the camera direction, so it stays right if the camera moves.
function startGlanceAway(st, cfg, rand) {
  st.glancingAway = true
  st.glanceAwayLeft = randIn(rand, cfg.glanceAwayLengthMin, cfg.glanceAwayLengthMax)
  // Lands like a look-around glance (Glance width/height), around the camera.
  st.gaYaw = (rand() * 2 - 1) * cfg.wanderYaw * DEG
  st.gaPitch = (rand() * 2 - 1) * cfg.wanderPitch * DEG
  const jump = angleBetweenDeg(0, 0, st.gaYaw, st.gaPitch)
  startGlide(st.eyeGlide, 1, 0)
  st.gaHeadMoved = !eyesOnlyFor(cfg, rand, jump)
  if (st.gaHeadMoved) {
    // The head follows Head Glance Amount of it, after a Glance Head Delay, at
    // Glance head speed (headGa: tracks camera + offset × amount each frame).
    queueHead(st, glanceHeadDelay(cfg, rand), { glideTo: 1, glideDur: 0, ga: true, smoothing: glanceSpeed(cfg, jump) })
  }
  if (jump > cfg.blinkOnJump) requestBlink(st)
}

function endGlanceAway(st, cfg, rand) {
  st.glancingAway = false
  st.glanceAwayIn = randIn(rand, cfg.glanceAwayEveryMin, cfg.glanceAwayEveryMax)
  startGlide(st.eyeGlide, 0, 0)
  // Back to the camera; a head that joined comes back after its glance delay
  // (one that hadn't started yet simply doesn't). Head speed again for eye contact.
  if (st.gaHeadMoved || st.headGa || st.headPending.active) {
    queueHead(st, glanceHeadDelay(cfg, rand), { glideTo: 0, glideDur: 0, ga: false, smoothing: cfg.headSpeed })
  }
  st.gaHeadMoved = false
}

// ── Checking in while wandering ───────────────────────────────────────────────
// A look-around glance at the camera: the eyes go to it, the head turns Head
// Glance Amount of the way toward it (blending its look-around direction with
// the camera) after a Glance Head Delay, unless it's eyes-only; a big one
// blinks. Then back to looking around. Not a state change: no engagement.
function startCheckIn(st, cfg, rand, scene) {
  st.checkingIn = true
  st.checkInLeft = randIn(rand, cfg.checkInLengthMin, cfg.checkInLengthMax)
  startGlide(st.eyeGlide, 0, 0)
  // A look-around glance whose target is the camera: same eyes-only chance,
  // head share (Head Glance Amount — of the way toward the camera), delay,
  // speed and blink.
  const jump = angleBetweenDeg(st.eyeYaw, st.eyePitch, scene.camYaw, scene.camPitch)
  st.ciHeadMoved = !eyesOnlyFor(cfg, rand, jump)
  if (st.ciHeadMoved) {
    queueHead(st, glanceHeadDelay(cfg, rand), {
      glideTo: 1 - clamp(cfg.headGlanceAmount, 0, 1), glideDur: 0, smoothing: glanceSpeed(cfg, jump),
    })
  }
  if (jump > cfg.blinkOnJump) requestBlink(st)
}

function endCheckIn(st, cfg, rand) {
  st.checkingIn = false
  st.checkInIn = randIn(rand, cfg.checkInEveryMin, cfg.checkInEveryMax)
  startGlide(st.eyeGlide, 1, 0)
  if (st.ciHeadMoved || st.headPending.active) {
    queueHead(st, glanceHeadDelay(cfg, rand), { glideTo: 1, glideDur: 0 })
  }
  st.ciHeadMoved = false
}

/**
 * Advance one frame.
 *
 * @param {object} st      from createAttention()
 * @param {object} cfg     settings (see ATTENTION_DEFAULTS)
 * @param {object} scene   lookTargetRuntime.scene — camera snapshot from last frame
 * @param {boolean} active node active (its `active` input AND node weight > 0)
 * @param {number} dt      seconds
 * @param {() => number} [rand]  injectable for tests
 * @param {boolean} [hold]  hold eye contact (the `holdEyeContact` input) — while
 *   on she goes into eye contact and nothing ends it; when it turns off, Lose
 *   interest after counts from that moment
 * @returns {object} st.out
 */
export function stepAttention(st, cfg, scene, active, dt, rand = Math.random, hold = false) {
  const out = st.out
  out.blink = false
  out.transition = null
  st.time += dt
  st.modeTime += dt

  // Gate: fades over releaseTime both ways, so nothing snaps.
  const rate = dt / Math.max(cfg.releaseTime, 0.01)
  st.gate = active ? Math.min(1, st.gate + rate) : Math.max(0, st.gate - rate)

  // Camera movement: "moved" fires each time it strays noticeMove from where
  // it last settled, so a slow drift counts too. Tracked even while inactive,
  // so waking up knows how long ago the camera really moved — a move made
  // during a long inactive stretch is old news, not a fresh "you did something".
  let moved = false
  if (scene.valid) {
    if (!st.anchorSet) {
      st.anchorSet = true
      st.ax = scene.camX; st.ay = scene.camY; st.az = scene.camZ
      st.lastMoveAt = st.time // the camera "arriving" counts as a move
      moved = true
    } else {
      const d = Math.hypot(scene.camX - st.ax, scene.camY - st.ay, scene.camZ - st.az)
      if (d > cfg.noticeMove) {
        st.ax = scene.camX; st.ay = scene.camY; st.az = scene.camZ
        st.lastMoveAt = st.time
        moved = true
      }
    }
  }

  // A user camera drag is ONE gesture: while the button is held — and while the
  // camera is still moving at all (momentum after the release, a scripted
  // move) — the camera counts as active, so "lose interest" and the settle
  // blink only start once things have come to rest.
  const held = !!scene.cameraHeld
  const interacting = held || !!scene.cameraMoving
  if (scene.valid && interacting) st.lastMoveAt = st.time
  const newGrab = held && scene.grabId !== st.lastGrabId
  if (held) st.lastGrabId = scene.grabId

  // Hold eye contact. Releasing it restarts the boredom clock, so she doesn't
  // drop eye contact the instant speech ends — from there the camera and Lose
  // interest after take over again.
  const holding = !!(active && hold)
  if (st.holding && !holding) st.lastMoveAt = st.time
  st.holding = holding

  if (!active) {
    if (st.mode !== 'inactive') setMode(st, 'inactive')
  } else if (scene.valid) {
    const lim = cfg.noticeAngle * DEG
    if (holding && st.mode !== 'engaged') enterEngaged(st, cfg, rand)
    const inView = Math.abs(scene.camYaw) <= lim && Math.abs(scene.camPitch) <= lim

    if (st.mode === 'inactive') {
      // Waking up: straight into eye contact if the camera just moved in view,
      // otherwise start glancing around from wherever things were left.
      if (inView && st.time - st.lastMoveAt < REACTIVATE_RECENT) enterEngaged(st, cfg, rand)
      else enterWandering(st, cfg, rand, scene, false)
    } else if (st.mode === 'wandering') {
      if (moved && inView) {
        enterEngaged(st, cfg, rand)
      } else if (st.checkingIn) {
        st.checkInLeft -= dt
        if (st.checkInLeft <= 0) endCheckIn(st, cfg, rand)
      } else {
        // Now and then, a brief look back to see if you're still there (only
        // if the camera is in view — otherwise it waits until it is).
        if (cfg.checkInEveryMax > 0) st.checkInIn -= dt
        if (cfg.checkInEveryMax > 0 && st.checkInIn <= 0 && inView) {
          startCheckIn(st, cfg, rand, scene)
        } else {
          st.holdLeft -= dt
          if (st.holdLeft <= 0) pickGlance(st, cfg, rand, false)
        }
      }
    } else if (st.mode === 'engaged') {
      st.outOfViewFor = inView ? 0 : st.outOfViewFor + dt
      const stillFor = st.time - st.lastMoveAt
      const bored = stillFor >= cfg.loseInterestAfter
      const unreachable = st.outOfViewFor >= cfg.giveUpAfter
      if (!holding && st.modeTime >= cfg.minEyeContact && (bored || unreachable)) {
        enterWandering(st, cfg, rand, scene, true)
      } else {
        // Brief glances away — not while the camera is being dragged or is
        // moving (she's following you then), and a drag cuts one short.
        if (interacting || cfg.glanceAwayEveryMax <= 0) {
          if (st.glancingAway) endGlanceAway(st, cfg, rand)
        } else if (st.glancingAway) {
          st.glanceAwayLeft -= dt
          if (st.glanceAwayLeft <= 0) endGlanceAway(st, cfg, rand)
        } else {
          st.glanceAwayIn -= dt
          if (st.glanceAwayIn <= 0) startGlanceAway(st, cfg, rand)
        }
        if (st.glancingAway) {
          st.eyeYaw = scene.camYaw + st.gaYaw
          st.eyePitch = scene.camPitch + st.gaPitch
        }
      }
      if (st.mode === 'engaged' && !st.glancingAway && !st.settledBlinkDone && !interacting
        && st.modeTime > SETTLE_GRACE && scene.gazeErrorDeg <= SETTLE_DEG) {
        st.settledBlinkDone = true
        if (cfg.blinkOnSettle) requestBlink(st)
      }
    }
    if (newGrab && cfg.blinkOnGrab && inView) requestBlink(st)
    // The head's delayed move, once its delay has run out.
    const hp = st.headPending
    if (hp.active) {
      hp.left -= dt
      if (hp.left <= 0) applyHead(st)
    }
    // Tracking a brief glance away: camera direction + its share of the offset.
    if (st.headGa) {
      const share = clamp(cfg.headGlanceAmount, 0, 1)
      st.headYaw = scene.camYaw + st.gaYaw * share
      st.headPitch = scene.camPitch + st.gaPitch * share
    }
  }

  // Engaged glides to the camera (away 0); wandering to the glance (away 1).
  // Inactive just finishes any glide in progress and then holds, so the
  // fade-out keeps the current gaze.
  stepGlide(st.headGlide, dt)
  stepGlide(st.eyeGlide, dt)
  out.gate = st.gate
  out.away = st.headGlide.v
  out.eyeAway = st.eyeGlide.v
  out.headYaw = st.headYaw
  out.headPitch = st.headPitch
  out.eyeYaw = st.eyeYaw
  out.eyePitch = st.eyePitch
  out.headSmoothing = st.headSmoothing
  return out
}
